import { BRIEF_EXPORT_CONCURRENCY, BRIEF_EXPORT_RENDER_TIMEOUT_MS } from '@sigora/domain';
import { useCallback, useEffect, useRef, useState } from 'react';

import { BriefExportBusyError, downloadBriefPdf } from '../../api/brief-export.ts';
import { isApiError } from '../../api/errors.ts';
import { Pending } from '../../ui/Pending.tsx';
import {
  BRIEF_EXPORT_BUSY,
  BRIEF_EXPORT_FAILED,
  BRIEF_EXPORT_NOT_PUBLISHED,
  BRIEF_EXPORT_PENDING,
  BRIEF_EXPORT_TRY_AGAIN,
  DOWNLOAD_PDF_LABEL,
  briefExportDone,
} from './stakeholder-copy.ts';

/** What the page allows beyond the server's own worst case: the request's way there and back. */
const BRIEF_EXPORT_DEADLINE_MARGIN_MS = 30_000;

/**
 * How long a PDF is waited for before the request is given up as failed: long enough for every
 * render the server runs or queues ahead of this one to take its whole deadline, and this one too.
 */
export const BRIEF_EXPORT_DEADLINE_MS =
  (BRIEF_EXPORT_CONCURRENCY.active + BRIEF_EXPORT_CONCURRENCY.waiting) *
    BRIEF_EXPORT_RENDER_TIMEOUT_MS +
  BRIEF_EXPORT_DEADLINE_MARGIN_MS;

/**
 * How long a saved file's object URL is kept after its click: a browser may read it a moment after
 * the click rather than during it.
 */
export const BRIEF_EXPORT_REVOKE_DELAY_MS = 10_000;

type ExportState =
  | { status: 'idle' }
  | { status: 'preparing' }
  | { status: 'done'; version: number }
  | { status: 'failed' }
  | { status: 'busy'; ready: boolean }
  | { status: 'not_published' };

/**
 * Download PDF: asks the server for a snapshot of the study's current Brief as a dated PDF and saves
 * it. While it is being made the button waits and says so; once saved it says which version the
 * file holds — the one the server captured, which a revision landing meanwhile can leave behind
 * the page — and the page is not read again. A PDF that could not be made, a network failure or
 * no answer within `BRIEF_EXPORT_DEADLINE_MS` says so, and the button becomes Try again; a server
 * busy with other PDFs says so, and Try again waits for as long as the server asked. A study with
 * no published Brief says so with nothing to try, and a signed-out session goes to the login view.
 *
 * One button stands for every state, and focus that was on it when it was pressed is put back on
 * it when it can be pressed again, so a keyboard reader does not lose their place while it waits.
 * `hidden` takes the control out of sight and out of the accessibility tree — while a past version
 * is open — without stopping a PDF already being made. A request still out when the control goes
 * away is abandoned.
 */
export const BriefExport = ({ studyId, hidden = false }: { studyId: string; hidden?: boolean }) => {
  const [state, setState] = useState<ExportState>({ status: 'idle' });
  const inFlight = useRef<AbortController | null>(null);
  const button = useRef<HTMLButtonElement | null>(null);
  const control = useRef<HTMLDivElement | null>(null);
  /** Whether the reader's focus was on the button when it was pressed, and is to be put back. */
  const restoreFocus = useRef(false);
  const busyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Saved files' object URLs and the timers that revoke them. */
  const savedUrls = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const urls = savedUrls.current;
    return () => {
      inFlight.current?.abort();
      if (busyTimer.current !== null) {
        clearTimeout(busyTimer.current);
      }
      for (const [url, timer] of urls) {
        clearTimeout(timer);
        URL.revokeObjectURL(url);
      }
      urls.clear();
    };
  }, []);

  /** Hands a file to the browser to save, its object URL revoked a moment after the click. */
  const saveFile = useCallback((blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.hidden = true;
    document.body.append(link);
    try {
      link.click();
    } finally {
      link.remove();
      savedUrls.current.set(
        url,
        setTimeout(() => {
          savedUrls.current.delete(url);
          URL.revokeObjectURL(url);
        }, BRIEF_EXPORT_REVOKE_DELAY_MS),
      );
    }
  }, []);

  const download = useCallback(async () => {
    const controller = new AbortController();
    inFlight.current = controller;
    const deadline = setTimeout(() => controller.abort(), BRIEF_EXPORT_DEADLINE_MS);
    if (busyTimer.current !== null) {
      clearTimeout(busyTimer.current);
      busyTimer.current = null;
    }
    restoreFocus.current = document.activeElement === button.current;
    setState({ status: 'preparing' });

    try {
      const pdf = await downloadBriefPdf(studyId, controller.signal);
      saveFile(pdf.blob, pdf.filename);
      setState({ status: 'done', version: pdf.version });
    } catch (error) {
      if (error instanceof BriefExportBusyError) {
        setState({ status: 'busy', ready: false });
        busyTimer.current = setTimeout(() => {
          busyTimer.current = null;
          setState({ status: 'busy', ready: true });
        }, error.retryAfterSeconds * 1000);
      } else if (isApiError(error) && error.status === 401) {
        setState({ status: 'idle' });
      } else if (isApiError(error) && error.code === 'brief_not_published') {
        setState({ status: 'not_published' });
      } else {
        setState({ status: 'failed' });
      }
    } finally {
      clearTimeout(deadline);
      inFlight.current = null;
    }
  }, [studyId, saveFile]);

  const disabled =
    state.status === 'preparing' ||
    state.status === 'not_published' ||
    (state.status === 'busy' && !state.ready);
  const retry = state.status === 'failed' || state.status === 'busy';

  // A disabled button loses focus, so once it can be pressed again it takes back the focus it had,
  // unless the reader has put their focus somewhere else meanwhile.
  useEffect(() => {
    if (!restoreFocus.current || disabled) {
      return;
    }
    restoreFocus.current = false;
    const active = document.activeElement;
    if (active === null || active === document.body || control.current?.contains(active) === true) {
      button.current?.focus();
    }
  }, [state, disabled]);

  return (
    <div ref={control} className="brief-export" data-test-id="brief-export-control" hidden={hidden}>
      <button
        ref={button}
        type="button"
        data-test-id={retry ? 'brief-export-retry' : 'brief-export'}
        disabled={disabled}
        onClick={() => {
          void download();
        }}
      >
        {retry ? BRIEF_EXPORT_TRY_AGAIN : DOWNLOAD_PDF_LABEL}
      </button>
      {state.status === 'preparing' && (
        <Pending className="brief-export-line" testId="brief-export-pending">
          {BRIEF_EXPORT_PENDING}
        </Pending>
      )}
      {state.status === 'done' && (
        <p className="brief-export-line" role="status" data-test-id="brief-export-done">
          {briefExportDone(state.version)}
        </p>
      )}
      {state.status === 'failed' && (
        <p className="brief-export-line chat-error" role="alert" data-test-id="brief-export-failed">
          {BRIEF_EXPORT_FAILED}
        </p>
      )}
      {state.status === 'busy' && (
        <p className="brief-export-line" role="alert" data-test-id="brief-export-busy">
          {BRIEF_EXPORT_BUSY}
        </p>
      )}
      {state.status === 'not_published' && (
        <p className="brief-export-line" role="status" data-test-id="brief-export-not-published">
          {BRIEF_EXPORT_NOT_PUBLISHED}
        </p>
      )}
    </div>
  );
};
